import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';

import { SaveFeedback } from '../../ui/action-feedback.js';
import type { AutosaveStatus } from '../../lib/autosave.js';
import { useDianaUIPortal } from '../../ui/index.js';

export function ClientDetailsPanel({
  triggerRef,
  open,
  onOpenChange,
  conflicted,
  title,
  status,
  onRetry,
  error,
  onSave,
  dirty,
  children,
  focusLabel = 'Name',
}: {
  triggerRef: RefObject<HTMLButtonElement | null>;
  open: boolean;
  onOpenChange(open: boolean): void;
  conflicted: boolean;
  title: string;
  status: AutosaveStatus;
  error?: string;
  onRetry(): void;
  onSave(): Promise<void>;
  dirty: boolean;
  children: ReactNode;
  focusLabel?: string;
}) {
  const portal = useDianaUIPortal();
  const content = useRef<HTMLDivElement>(null);
  const { panelRef, bodyRef } = useEditPanelViewport(open && !conflicted);
  const capturePanel = useCallback(
    (node: HTMLDivElement | null) => {
      content.current = node;
      panelRef(node);
    },
    [panelRef],
  );
  useLayoutEffect(() => {
    if (conflicted && open) onOpenChange(false);
  }, [conflicted, open, onOpenChange]);
  return (
    <Dialog.Root open={open && !conflicted} onOpenChange={onOpenChange}>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="client-panel__overlay" />
        <Dialog.Content
          ref={capturePanel}
          className="client-panel dui-edit-panel"
          data-edit-panel="true"
          onOpenAutoFocus={(event) => {
            const panel = content.current;
            if (!panel) return;
            if (window.matchMedia('(max-width: 1023px)').matches) {
              event.preventDefault();
              panel
                .querySelector<HTMLElement>('[aria-label="Close client details"]')
                ?.focus({ preventScroll: true });
              return;
            }
            const field = [
              ...panel.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input,textarea'),
            ].find((el) => el.getAttribute('aria-label') === focusLabel);
            if (field) {
              event.preventDefault();
              field.focus();
            }
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!conflicted) triggerRef.current?.focus();
          }}
        >
          <header className="client-panel__header" data-panel-header>
            <div>
              <p>{title}</p>
              <Dialog.Title>Client details</Dialog.Title>
            </div>
            <Dialog.Close
              className="client-button client-button--icon"
              aria-label="Close client details"
            >
              <X size={20} aria-hidden="true" />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            Press Save to apply your changes. Closing asks before discarding unsaved changes.
          </Dialog.Description>
          <div ref={bodyRef} className="client-panel__body" data-panel-body>
            {children}
          </div>
          <footer className="client-panel__footer" data-panel-footer>
            <SaveFeedback status={status} error={error} onRetry={onRetry} />
            <button
              type="button"
              className="client-button client-button--primary"
              aria-busy={status === 'Saving'}
              disabled={!dirty || status === 'Saving'}
              onClick={() => void onSave()}
            >
              {status === 'Saving' ? 'Saving…' : 'Save'}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
